import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, User, LogOut, BookMarked } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Discover", to: "/" },
  { label: "Civilisation", to: "/civilisation" },
  { label: "Traditions", to: "/traditions" },
  { label: "Philosophy", to: "/philosophy" },
  { label: "Library", to: "/library" },
  { label: "Atlas", to: "/atlas" },
  { label: "Timelines", to: "/timelines" },
  { label: "Mandala", to: "/mandala" },
  { label: "Critical Studies", to: "/critical-studies" },
  { label: "Learn", to: "/learn" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `px-2.5 py-1.5 text-[13px] font-sans font-medium tracking-wide transition-colors rounded-sm ${
      isActive ? "text-indic-gold" : "text-slate-300 hover:text-amber-50"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md bg-[#0B1021]/90 border-b border-indic-gold/15" : "bg-gradient-to-b from-[#0B1021]/90 to-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 flex items-center justify-between h-16 lg:h-[72px]">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-indic-gold/50 text-indic-gold">
            <span className="font-display text-lg leading-none">इ</span>
          </span>
          <span className="leading-tight">
            <span className="block font-display text-base sm:text-lg tracking-[0.1em] text-amber-50 group-hover:text-indic-gold transition-colors whitespace-nowrap">Indic Civilisation</span>
            <span className="block text-[9px] font-sans uppercase tracking-[0.28em] text-slate-400">Mahakali Tribunal</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} end={n.to === "/"}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/search")} className="p-2 text-slate-300 hover:text-indic-gold transition-colors" aria-label="Search" data-testid="search-icon-btn">
            <Search size={18} />
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-9 w-9 items-center justify-center rounded-full bg-indic-gold/15 border border-indic-gold/40 text-indic-gold text-sm font-semibold hover:bg-indic-gold/25 transition-colors" data-testid="user-menu-btn">
                  {(user.name || user.email || "?").charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-indic-card border-indic-card-border text-slate-200 w-52">
                <div className="px-2 py-2">
                  <p className="text-sm font-medium text-amber-50 truncate">{user.name || "Reader"}</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-indic-card-border" />
                <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer gap-2" data-testid="menu-account">
                  <User size={15} /> My Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/account?tab=bookmarks")} className="cursor-pointer gap-2" data-testid="menu-bookmarks">
                  <BookMarked size={15} /> Bookmarks
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-indic-card-border" />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer gap-2 text-indic-terracotta" data-testid="menu-logout">
                  <LogOut size={15} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden sm:inline-flex items-center rounded-sm border border-indic-gold/50 px-4 py-1.5 text-[13px] font-sans font-medium text-indic-gold hover:bg-indic-gold hover:text-indic-indigo transition-colors" data-testid="signin-btn">
              Sign in
            </Link>
          )}

          <button onClick={() => setOpen(true)} className="xl:hidden p-2 text-slate-200" aria-label="Menu" data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0B1021] xl:hidden" data-testid="mobile-menu-panel">
          <div className="flex items-center justify-between h-16 px-4 border-b border-indic-gold/15">
            <span className="font-display text-xl tracking-[0.12em] text-amber-50">Indic Civilisation</span>
            <button onClick={() => setOpen(false)} className="p-2 text-slate-200" data-testid="mobile-menu-close"><X size={22} /></button>
          </div>
          <nav className="flex flex-col p-4 gap-1 overflow-y-auto">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} end={n.to === "/"}
                className={({ isActive }) => `px-3 py-3 font-serif text-xl border-b border-indic-card-border ${isActive ? "text-indic-gold" : "text-slate-200"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/scholar" onClick={() => setOpen(false)} className="px-3 py-3 font-serif text-xl text-indic-gold border-b border-indic-card-border">INDIC Scholar</Link>
            {!user && <Link to="/login" onClick={() => setOpen(false)} className="mt-4 text-center rounded-sm bg-indic-gold px-4 py-3 font-sans font-semibold text-indic-indigo">Sign in</Link>}
          </nav>
        </div>
      )}
    </header>
  );
}
